import { forEach, keys } from 'lodash'
import util from './util';
import Router from './router';
import { ISecurityContext } from './services/securityContext';
import { StoreInstance } from './store';
import { IOperationsHelper } from './services/operationsHelper';

const pendingFactories = {};

const fillFactoryParams = function (cs, factorySpec) {
	const params: any[] = [];

	forEach(factorySpec.paramNames, paramName => {
		if (cs[paramName]) {
			params.push(() => { return cs[paramName]; });
		}
	});

	return params;
};

const processFactory = function (cs, factorySpec) {
	const params = fillFactoryParams(cs, factorySpec);

	if (params.length === factorySpec.paramNames.length) {
		return factorySpec.factory.apply(factorySpec.factory, params);
	}

	return undefined;
};

const processFactories = function (cs) {
	forEach(keys(pendingFactories), factoryKey => {
		const pendingFactory = pendingFactories[factoryKey];
		const service = pendingFactory && processFactory(cs, pendingFactory);

		if (service) {
			delete pendingFactories[factoryKey];
			cs.set(factoryKey, service);
		}
	});
};

export interface IComponentServices {
	readonly operationsHelper: IOperationsHelper;
	readonly route: any;
	readonly router: typeof Router;
	readonly routeParams: any;
	readonly securityContext: ISecurityContext;
	readonly store: StoreInstance;
	set(key: string, service: (() => any) | Record<string, any>, paramNames?: string[], force?: boolean): IComponentServices
}

const componentServices: Record<string, any> = {
	// paramNames can be specified in the same order as the factory to avoid problems with minifiers renaming parameters
	set(key: string, service: (() => any) | Record<string, any>, paramNames?: string[], force?: boolean) {
		function setInstance(instance) {
			if (force || instance !== componentServices[key]) {
				Object.defineProperty(componentServices, key, { enumerable: true, configurable: true, value: instance });
				processFactories(componentServices);
			}
		}

		if (typeof service === 'function') {
			if (!pendingFactories[key]) {
				const factorySpec = { paramNames: paramNames || util.getParamNames(service), factory: service };
				const instance = processFactory(componentServices, factorySpec);

				if (instance) {
					setInstance(instance);
				}
				else {
					pendingFactories[key] = factorySpec;
				}
			}
		}
		else {
			setInstance(service || componentServices[key]);
		}

		return componentServices;
	}
};

export default (componentServices as IComponentServices);